<template>
    <v-col>
        <v-row v-if="isLoaded && current_invoice" no-gutters>
            <v-col cols="12">
                <div class="d-flex justify-space-between my-2">
                    <InvoiceStatusInfoBlock
                        :exported-at="current_invoice.exported_at"
                        :exported-to="current_invoice.exported_to"
                        :sent-at="current_invoice.sent_at"
                        :sent-from="current_invoice.sent_from"
                        :paid-at="current_invoice.paid_at"
                        :paid-sum="current_invoice.paid_sum"
                        :paid-currency="current_invoice.currency"
                        :annulled-at="current_invoice.annulled_at"
                        :show-status="!isEditing"
                        :invoice-status="current_invoice.status"
                        :invoice-process-status="current_invoice.process_status"
                    >
                    </InvoiceStatusInfoBlock>
                    <v-spacer></v-spacer>
                    <InvoiceActionsToolbar
                        :show-status="!isEditing"
                        :is-editing="isEditing"
                        :invoice-status="current_invoice.status"
                        :invoice-process-status="current_invoice.process_status"
                        @save="onSaveClick"
                        @cancel="onCancelEditClick"
                    >
                        <template slot="not-edit-buttons">
                            <v-btn
                                v-if="process_error_message && is_processing && current_invoice.id && canEditAccount"
                                color="primary"
                                class="white--text mx-1"
                                @click="onClearErrorsClick"
                            >
                                {{ $t('Clear errors') }}
                            </v-btn>
                            <v-btn
                                v-if="!is_open && !isEditing && !process_error_message"
                                color="primary"
                                outlined
                                class="mx-1"
                                @click="onDownloadInvoiceClick(current_invoice.id)"
                            >
                                {{ $t('Download pdf') }}
                            </v-btn>
                            <v-btn
                                v-if="is_open && !is_processing && !isEditing && !has_errors && canEditInvoice"
                                color="primary"
                                class="white--text mx-1"
                                @click="onCreateInvoiceClick()"
                            >
                                {{ $t('Create invoice') }}
                            </v-btn>
                            <v-btn
                                v-if="current_invoice.status === 'created' && !isEditing && !process_error_message && !is_processing && canEditAccount && can_send_invoices_to_clients(invoiceAccount)"
                                color="primary"
                                class="white--text mx-1"
                                @click="onSendInvoicesClick()"
                            >
                                {{ $t('Send to client') }}
                            </v-btn>
                            <v-btn
                                v-if="['created', 'sent'].includes(current_invoice.status) && !isEditing && !process_error_message && !is_processing && canEditInvoice"
                                color="primary"
                                class="white--text mx-1"
                                @click="onMarkPaidClick"
                            >
                                {{ $t('Mark paid') }}
                            </v-btn>
                            <v-btn
                                v-if="!is_processing && !isEditing && !has_errors && (canEditAccount || canEditInvoice)"
                                class="mr-0 primary--text px-0 mx-1"
                                outlined
                                color="primary"
                                style="min-width: 40px !important;"
                                @click="onEditInvoiceClick"
                            >
                                <v-icon>edit</v-icon>
                            </v-btn>
                            <v-menu
                                v-if="!process_error_message && canEditAccount || current_invoice.is_invoice_owner"
                                content-class="overflow-y-hidden dropmenu"
                                bottom
                                offset-y
                                left
                            >
                                <template v-slot:activator="{ on }">
                                    <v-btn class="mr-3 ml-1" color="n3" small icon v-on="on">
                                        <v-icon>more_vert</v-icon>
                                    </v-btn>
                                </template>
                                <v-list dense>
                                    <v-list-item v-if="can_export_invoice"
                                                 class="menu-list-item" style="height: 32px !important; min-height: 32px !important;" @click="onExportInvoicesClick()">
                                        <v-list-item-content>
                                            <v-list-item-title>
                                                {{ $t('Export to {0}').format(selectedVendorName) }}
                                            </v-list-item-title>
                                        </v-list-item-content>
                                    </v-list-item>

                                    <v-list-item v-if="can_update_invoice_status"
                                                 class="menu-list-item" style="height: 32px !important; min-height: 32px !important;" @click="onUpdateInvoiceStatusClick()">
                                        <v-list-item-content>
                                            <v-list-item-title>
                                                {{ $t('Update status from {0}').format(selectedVendorName) }}
                                            </v-list-item-title>
                                        </v-list-item-content>
                                    </v-list-item>

                                    <v-list-item v-if="can_send_invoice"
                                                 class="menu-list-item" style="height: 32px !important; min-height: 32px !important;" @click="onSendInvoicesClick">
                                        <v-list-item-content>
                                            <v-list-item-title>
                                                {{ $t('Send to client') }}
                                            </v-list-item-title>
                                        </v-list-item-content>
                                    </v-list-item>

                                    <v-list-item v-if="canMarkUnpaidInvoice"
                                                 class="menu-list-item" style="height: 32px !important; min-height: 32px !important;" @click="onMarkUnpaidClick">
                                        <v-list-item-content>
                                            <v-list-item-title>
                                                {{ $t('Mark unpaid') }}
                                            </v-list-item-title>
                                        </v-list-item-content>
                                    </v-list-item>

                                    <v-list-item v-if="is_open && !is_processing && canEditInvoice && current_invoice.contract"
                                                 class="menu-list-item" style="height: 32px !important; min-height: 32px !important;"
                                                 @click="onShowAllLinesClick">
                                        <v-list-item-content>
                                            <v-list-item-title>
                                                {{ $t('Show all lines') }}
                                            </v-list-item-title>
                                        </v-list-item-content>
                                    </v-list-item>

                                    <v-list-item v-if="current_invoice.status !== 'annulled'"
                                                 class="menu-list-item" style="height: 32px !important; min-height: 32px !important;" @click="onVoidInvoiceClick">
                                        <v-list-item-content>
                                            <v-list-item-title>
                                                {{ $t('Void invoice') }}
                                            </v-list-item-title>
                                        </v-list-item-content>
                                    </v-list-item>

                                    <v-list-item v-if="current_invoice.status === 'annulled'"
                                                 class="menu-list-item" style="height: 32px !important; min-height: 32px !important;" @click="onRestoreInvoiceClick">
                                        <v-list-item-content>
                                            <v-list-item-title>
                                                {{ current_invoice.contract ? $t('Calculate invoice') : $t('Restore invoice') }}
                                            </v-list-item-title>
                                        </v-list-item-content>
                                    </v-list-item>

                                    <v-list-item v-if="!!!current_invoice.contract"
                                                 class="menu-list-item" style="height: 32px !important; min-height: 32px !important;" @click="onDeleteInvoicesClick">
                                        <v-list-item-content>
                                            <v-list-item-title>
                                                {{ $t('Delete invoice') }}
                                            </v-list-item-title>
                                        </v-list-item-content>
                                    </v-list-item>

                                </v-list>
                            </v-menu>
                        </template>
                    </InvoiceActionsToolbar>
                </div>
            </v-col>

            <v-col class="mt-1 pt-3" md="12">
                <div v-if="process_error_message || current_invoice">
                    <InvoiceAlertToolbar
                        :show-validation-error="has_errors && !is_processing && current_invoice.id"
                        :validation-message="warning"
                        :warning-message="warning"
                        :process-error-message="process_error_message"
                    >
                    </InvoiceAlertToolbar>
                </div>

                <v-card class="pa-5 override">

                    <!-- invoice header -->
                    <v-row no-gutters class="ma-5">
                        <v-col md="6" cols="12" class="pa-4 pr-5 tofrom">
                            <InvoiceSellerInfoBlock
                                :invoice="current_invoice"
                                :translations="translations"
                            />
                            <InvoiceBankAccounts
                                :invoice="current_invoice"
                                :translations="translations"
                            />
                            <InvoiceSettingsFields
                                :is-editing="isEditing"
                                :due-date.sync="current_invoice.due_date"
                                :invoice-date.sync="current_invoice.invoice_date"
                                :reference-number.sync="current_invoice.reference_number"
                                :invoice-number.sync="current_invoice.invoice_number"
                                :invoice-number-error="current_invoice.invoice_number_error"
                                :due-date-error="current_invoice.due_date_error"
                                :invoice-date-error="current_invoice.invoice_date_error"
                                :translations="translations"
                            />
                        </v-col>

                        <v-col md="6" cols="6" class="pa-4 text-right">
                            <InvoiceSellerAvatar
                                :is-editing="isEditing"
                                :invoice="current_invoice"
                                :translations="translations"
                            />
                            <InvoiceClientInfoBlock
                                :invoice="current_invoice"
                                :translations="translations"
                            />
                        </v-col>
                    </v-row>
                    <!-- // invoice header -->

                    <!-- invoice rows -->
                    <v-row no-gutters class="mx-5">
                        <v-col cols="12" class="pr-4 pt-4 pb-2" style="padding-left: 10px">
                            <v-data-table
                                hide-default-footer
                                :items="current_invoice.rows"
                                :headers="headers"
                                :items-per-page="-1"
                                class="no-divider-lines field-edit-table"
                            >
                                <template v-slot:body="{ items }">
                                    <draggable
                                        :list="items"
                                        :disabled="!isEditing"
                                        tag="tbody"
                                        @update="onUpdate(items)"
                                    >
                                        <template v-for="(item, index) in items">
                                            <SalesInvoiceEditRow
                                                :key="index"
                                                :row="item"
                                                :index="index"
                                                :taxes="taxes"

                                                :is-open="is_open"
                                                :is-editing="isEditing"

                                                :currency="currency"
                                                :locale-code="locale_code"
                                                :account-id="current_invoice.account_id"
                                                :can-confirm-invoice-rows="canConfirmInvoiceRows"
                                                :product-field-resources="productFieldResources"
                                                :invoice-id="current_invoice.id"
                                                :can-edit-account="canEditAccount"
                                                @delete_row="onDeleteRow"
                                                @add_new_product="onAddProductClick"
                                            />
                                        </template>
                                    </draggable>
                                </template>
                            </v-data-table>
                        </v-col>
                    </v-row>
                    <!-- // invoice rows -->

                    <!-- invoice summary -->
                    <v-row no-gutters class="mx-5">
                        <v-col class="mr-3">
                            <v-row no-gutters>
                                <v-col class="pl-4 pt-4" cols="1">
                                    <v-btn v-if="isEditing" text color="primary" class="ma-0" @click="addRow()">
                                        <v-icon left>mdi-plus-box</v-icon>
                                        {{ $t("Add row") }}
                                    </v-btn>
                                </v-col>
                                <v-col offset="6" cols="2" class="text-right subheading px-3 mt-4 mb-2">
                                    {{ translations.subtotal }}
                                </v-col>
                                <v-col cols="3" class="text-right subheading mt-4 px-5 mb-2">
                                    {{ formatMoney(total_net, current_invoice.currency) }}
                                </v-col>

                                <template v-for="(value, key) in taxesSummary">
                                    <v-col :key="key" offset="7" cols="2" class="text-right subheading px-3">
                                        {{ key }}
                                    </v-col>
                                    <v-col :key="'cur' + key" cols="3" class="text-right subheading px-5">
                                        {{ formatMoney(value, current_invoice.currency) }}
                                    </v-col>
                                </template>

                                <v-col offset="7" cols="2" class="text-right subheading font-weight-medium px-3 mt-2">
                                    {{ translations.total }}
                                </v-col>
                                <v-col cols="3" class="text-right subheading font-weight-medium px-5 mt-2">
                                    {{ formatMoney(current_invoice.total_sum, current_invoice.currency) }}
                                </v-col>
                            </v-row>
                        </v-col>
                    </v-row>
                    <!-- // invoice summary -->

                    <!-- invoice footer -->
                    <v-row no-gutters class="mx-5">
                        <v-col class="pa-5 mt-5" cols="12">
                            <RichTextEditor
                                ref="richTextEditor"
                                :content="current_invoice.comments"
                                toolbar="#sales-invoice-comment-toolbar"
                                :disabled="!isEditing"
                                @input="current_invoice.comments = $event"
                            />
                            <div v-show="isEditing" class="mt-2">
                                <RichTextEditorToolbar id="sales-invoice-comment-toolbar"/>
                            </div>
                        </v-col>
                    </v-row>
                    <!-- // invoice footer -->

                </v-card>
            </v-col>
        </v-row>
        <v-row v-else>
            <v-col class="mt-4 pt-3" md="12">{{ $t("Loading invoice...") }}</v-col>
        </v-row>

        <EditCompanyProductDialog
            v-if="showProductDialog"
            :company-id="current_invoice.account_id"
            @close_modal="showProductDialog = false"
        />
    </v-col>
</template>

<script>
import Vue from "vue";
import SalesInvoiceEditRow from "./SalesInvoiceEditRow.vue";
import InvoiceAlertToolbar from "@/components/Invoice/InvoiceAlertToolbar";
import EditCompanyProductDialog from "@/views/Dialog/EditProductDialog/EditCompanyProductDialog.vue";
import DotStatus from "../../components/DotStatus";
import {$t, $tc} from "@/i18n";
import {reactive, toRefs, computed, onMounted, onUnmounted, ref} from "@vue/composition-api";
import SalesInvoiceModule from "../../store/modules/sales_invoice";

import SendInvoicesDialog from "./SendInvoicesDialog";
import DeleteInvoicesDialog from "./DeleteInvoicesDialog";
import ExportInvoicesDialog from "./ExportInvoicesDialog";

import {Router} from "@/composables/Router";
import DialogModule from "../../store/modules/dialog";
import AppModule, {myDebug} from "../../store/modules/app";
import AccountModule, {state} from "../../store/modules/account";
import ClientModule from "../../store/modules/client";
import ProductModule from "../../store/modules/product";
import ProductFieldModule from "../../store/modules/product_field";
import ContractModule from "../../store/modules/contract";
import BreadcrumbsModule from "../../store/modules/breadcrumbs";
import {addDays} from "date-fns";
import {getRegionAppTitle} from "@/helpers/helpers";
import {getCurrencySymbolFromCurrency} from "@/helpers/formatting";
import {formatMoney, formatSysDate} from "@/plugins/formatting";
import InvoiceActionsToolbar from "@/components/Invoice/InvoiceActionsToolbar"
import InvoiceSellerInfoBlock from "@/components/Invoice/InvoiceSellerInfoBlock";
import InvoiceBankAccounts from "@/components/Invoice/InvoiceBankAccounts";
import InvoiceSettingsFields from "@/components/Invoice/InvoiceSettingsFields";
import InvoiceSellerAvatar from "@/components/Invoice/InvoiceSellerAvatar";
import InvoiceClientInfoBlock from "@/components/Invoice/InvoiceClientInfoBlock";
import InvoiceStatusInfoBlock from "@/components/Invoice/InvoiceStatusInfoBlock";
import RightsModule from "../../composables/use-rights";
import AccountAvatar from "../../components/AccountAvatar";
import {PRODUCT_FIELD_TYPES} from "../../helpers/constants";
import draggable from "vuedraggable";
import RichTextEditor from "../../components/RichTextEditor/RichTextEditor";
import RichTextEditorToolbar from "../../components/RichTextEditor/RichTextEditorToolbar";
import errorDialog from "@/views/ErrorDialog/errorDialog"
import ApiInvoice from "@/api/billing/invoice";
import WS from "../../utility/webSocket";
import ApiTax from "../../api/billing/tax";

export default {
    name: "SalesInvoiceEditView",
    components: {
        EditCompanyProductDialog,
        DotStatus,
        SalesInvoiceEditRow,
        InvoiceAlertToolbar,
        InvoiceActionsToolbar,
        InvoiceSellerInfoBlock,
        InvoiceBankAccounts,
        InvoiceSettingsFields,
        InvoiceSellerAvatar,
        InvoiceClientInfoBlock,
        InvoiceStatusInfoBlock,
        AccountAvatar,
        draggable,
        RichTextEditor,
        RichTextEditorToolbar
    },
    setup(props, ctx) {
        const isLoaded = ref(false)

        const router = Router();
        const richTextEditor = ref(null);
        const {can_edit_account, can_complete_invoice_rows, can_manage_client_invoices, can_edit_invoice_rows, can_send_invoices_to_clients} = RightsModule();
        const {
            openConfirmDialog, setConfirmOptions,
            openDialog, setDialogOptions,
            setErrorOptions,
            openError,
        } = DialogModule();
        const {available_accounts, isFeatureEnabled, fetchAccount, getAccount, current_account} = AccountModule();
        const {formatting} = AppModule();
        const {setBreadcrumbs} = BreadcrumbsModule();
        const showProductDialog = ref(false)
        const {
            fetchInvoice,
            saveSalesInvoice,
            getStatusColorMap,
            cancelSalesInvoices,
            restoreSalesInvoices,
            clearProcessErrors,
            markPaidInvoices,
            markUnpaidInvoices,
            getExportableInvoices,
            getSendableInvoices,

            deleteSalesInvoices,
            exportSalesInvoices,
            sendSalesInvoices,

            getUpdatableStatusInvoices
        } = SalesInvoiceModule();
        const {getClient, fetchClient, setCurrentClient} = ClientModule();
        const {fetchAccountProductFields} = ProductFieldModule();
        const {fetchClientBillingSettings} = ContractModule();
        const {fetchProducts} = ProductModule();

        const state = reactive({
            taxes: null,
            current_invoice: null,
            isEditing: false,
            canEditInvoice: false,
            in_progress_confirmed: false,
        });

        const invoiceId = ctx.root._route.params.id ? Number(ctx.root._route.params.id) : null;
        const accountId = computed(() => ctx.root._route.params.accountId ? Number(ctx.root._route.params.accountId) : null);
        const clientId = computed(() => ctx.root._route.params.clientId ? Number(ctx.root._route.params.clientId) : null);
        const invoiceClient = ref(null);
        const invoiceAccount = ref(null);
        const translations = ref(null);
        const clientBillingSettings = ref(null);

        const selected_invoices_list = computed(() => {
            return [state.current_invoice];
        });

        const canEditAccount = computed(() => {
            return can_edit_invoice_rows({id: state.current_invoice.account_id}, state.current_invoice.client_id)
        })
        const warning = computed(() => {
            if (state.current_invoice && state.current_invoice.invoice_errors && state.current_invoice.invoice_errors.length > 0) {
                return state.current_invoice.invoice_errors.join("<br>");
            }
            return "";
        });
        const navigateToList = function () {
            return router.push({name: 'client_profile_billing', params: {id: clientId.value}})
        }
        const onAddProductClick = () => {
            showProductDialog.value = true
        };
        const is_processing = computed(() => {
            return state.current_invoice && state.current_invoice.process_status;
        });
        const is_open = computed(() => {
            let status = state.current_invoice.status
            return !state.current_invoice || !status || ['pending', 'in_progress', 'calculated'].includes(status)
        });
        const has_errors = computed(() => {
            return (
                state.current_invoice &&
                state.current_invoice.invoice_errors &&
                state.current_invoice.invoice_errors.length > 0
            );
        });
        const process_error_message = computed(() => {
            if (
                !state.current_invoice.process_status ||
                !(state.current_invoice._data != null ? state.current_invoice._data.process_error : undefined)
            ) {
                return null;
            } else if (state.current_invoice._data.process_error.code === 403) {
                return $t("Invalid Scoro credentials");
            } else if (state.current_invoice._data.process_error.messages) {
                return state.current_invoice._data.process_error.messages.join("<br>");
            }
            return null;
        });
        const locale_code = computed(() => {
            return state.current_invoice.locale_code ? state.current_invoice.locale_code : "en_GB";
        });

        const parse_number = (x) => {
            return (isNaN(x) || !x) ? 0 : Math.round(parseFloat(x) * 100) / 100;
        };

        const parse_discount = (x) => {
            return (isNaN(x) || !x) ? 0 : Math.round(parseFloat(x) * 10000) / 10000;
        };

        const calculate = (field, row, tax_rate) => {
            let calc_quantity = parse_number(row.quantity);
            let calc_net_price = parse_number(row.net_price);
            let calc_discount_rate = parse_discount(row.discount_rate, 2);
            let original_sum = Math.round(calc_quantity * calc_net_price * 100) / 100;

            let discount_sum = Math.round(original_sum * calc_discount_rate * 100) / 100;
            if (field === 'discount_sum') return discount_sum;

            let net_sum = original_sum - discount_sum
            if (field === 'net_sum') return net_sum;

            // default we use ROUND HALF EVEN
            let tax_sum = tax_rate ? roundHalfEven(net_sum * tax_rate, 2) : 0;
            if (field === 'tax_sum') return tax_sum;
        };

        /**
         * Round Half-Even (Banker's Rounding) Utility
         * https://en.wikipedia.org/wiki/Rounding#Round_half_to_even
         *
         * @param {number} value - the value to operate on
         * @param {number} numDecimals - number of decimal places
         * @returns {number} `value` rounded to `numDecimal` places
         */
        const roundHalfEven = (value, numDecimals = 2) => {
            const isEven = (d) => d % 2 === 0;
            const MAX_DECIMALS_ALLOWED = 20;

            if (typeof value === "undefined") {
                throw new Error("value is required");
            }
            if (typeof value !== "number") {
                throw new Error("value must be a number type");
            }
            if (value < 0) {
                return -roundHalfEven(-value, numDecimals);
            }
            if (numDecimals === 0) {
                return roundHalfEven(value / 10, 1) * 10;
            }
            if (numDecimals > MAX_DECIMALS_ALLOWED) {
                throw new Error(`Cannot handle more than ${MAX_DECIMALS_ALLOWED} decimals`);
            }
            // convert to string; remove trailing 0s
            const isExponentialForm =
                value.toString().includes("e") || value.toString().includes("E");
            const strNum = (
                isExponentialForm
                    ? value.toFixed(MAX_DECIMALS_ALLOWED).toString()
                    : value.toString()
            ).replace(/0+$/, "");
            const decimalIndex = strNum.indexOf(".");
            if (decimalIndex < 0) {
                // no fractional part
                return value;
            }
            let intPart = strNum.slice(0, decimalIndex);
            if (intPart.length === 0) {
                intPart = 0;
            }
            let fractPart = strNum.slice(decimalIndex + 1); // extract fractional part
            if (fractPart.length < numDecimals) {
                return value;
            }
            const followingDig = parseInt(fractPart[numDecimals], 10);
            if (followingDig < 5) {
                // rounding not required
                const newFractPart = fractPart.slice(0, numDecimals);
                return parseFloat(`${intPart}.${newFractPart}`);
            }
            if (followingDig === 5) {
                const newFractPart = fractPart.slice(0, numDecimals + 1);
                if (parseInt(fractPart.slice(numDecimals + 1), 10) > 0) {
                    fractPart = `${newFractPart}9`;
                } else {
                    fractPart = newFractPart;
                }
            }
            let nextDig = parseInt(fractPart[fractPart.length - 1], 10);
            let carriedOver = 0;
            for (let ptr = fractPart.length - 1; ptr >= numDecimals; ptr--) {
                let dig = parseInt(fractPart[ptr - 1], 10) + carriedOver;
                if (nextDig > 5 || (nextDig === 5 && !isEven(dig))) {
                    ++dig;
                }
                if (dig > 9) {
                    dig -= 10;
                    carriedOver = 1;
                } else {
                    carriedOver = 0;
                }
                nextDig = dig;
            }
            let newFractPart = "";
            for (let ptr = numDecimals - 2; ptr >= 0; ptr--) {
                let d = parseInt(fractPart[ptr], 10) + carriedOver;
                if (d > 9) {
                    d -= 10;
                    carriedOver = 1;
                } else {
                    carriedOver = 0;
                }
                newFractPart = `${d}${newFractPart}`;
            }
            intPart = parseInt(intPart, 10) + carriedOver;
            return parseFloat(`${intPart}.${newFractPart}${nextDig}`);
        };

        const total_net = computed(() => {
            let total_net = 0;
            if (state.current_invoice && state.current_invoice.rows) {
                for (let row of Array.from(state.current_invoice.rows)) {
                    total_net += calculate('net_sum', row);
                }
            }
            return total_net;
        });

        const taxesSummary = computed(() => {
            const summary = {};
            if (!state.taxes) return summary;
            if (!state.current_invoice || !state.current_invoice.rows) return summary;

            let total = 0
            for (let row of Array.from(state.current_invoice.rows)) {
                let country_code = invoiceAccount.value.address_country_code.toLowerCase() ? invoiceAccount.value.address_country_code : ''
                let key, no_tax
                const taxes = state.taxes

                const tax = taxes && row && row.tax_id ? taxes.find(x => x.id === row.tax_id) : null
                let tax_rate = tax && tax.rate ? tax.rate : 0
                let tax_percent = tax && tax.percent ? tax.percent : ''

                let row_rate = row && row.tax_rate !== null ? row.tax_rate : 0
                let row_percent = row_rate ? (Math.round(1000000 * row_rate) / 10000) : ''

                // if invoice is "OPEN" then recalculate taxes values
                let is_recalculate = is_open.value || state.isEditing
                let percent = is_recalculate ? tax_percent : row_percent
                let rate = is_recalculate ? tax_rate : row_rate

                if (!percent) {
                    no_tax = true
                    key = translations.value.no_tax
                    if (!Object.prototype.hasOwnProperty.call(summary, key)) summary[key] = 0
                    summary[key] += calculate('net_sum', row)
                    total += calculate('net_sum', row)
                } else {
                    // if invoice is "CLOSED" then show saved values
                    if (row.tax_name) {
                        key = row.tax_name
                    } else {
                        key = translations.value.tax_name + " " + percent + "%"
                    }
                    if (!Object.prototype.hasOwnProperty.call(summary, key)) summary[key] = 0
                    summary[key] += calculate('tax_sum', row, rate)
                    total += (calculate('net_sum', row) + calculate('tax_sum', row, rate))
                }
            }
            state.current_invoice.total_sum = total

            return summary
        });

        const account_editable = false;
        const client_editable = false;
        const selected_account_obj = computed(() => {
            if (!state.current_invoice) {
                return null;
            }
            return available_accounts.value.find(element => {
                return element.id === state.current_invoice.account_id;
            });
        });

        const currency = computed(() => {
            return !!state.current_invoice && state.current_invoice.currency ? getCurrencySymbolFromCurrency(state.current_invoice.currency) : ''
        });
        const can_export_invoice = computed(() => {
            if (!state.current_invoice || !state.current_invoice.selected_vendor_type) return false
            return (
                !!state.current_invoice && state.current_invoice.id > 0 && canEditAccount.value
                && getExportableInvoices(selected_invoices_list.value).length
            );
        });

        const can_update_invoice_status = computed(() => {
            if (!state.current_invoice || !state.current_invoice.selected_vendor_type) return false
            return (
                !!state.current_invoice && state.current_invoice.id > 0 && canEditAccount.value
                && getUpdatableStatusInvoices(selected_invoices_list.value, state.current_invoice.selected_vendor_type).length
            );
        });

        const can_send_invoice = computed(() => {
            return (
                !!state.current_invoice && state.current_invoice.id > 0 && canEditAccount.value
                && getSendableInvoices(selected_invoices_list.value).length && can_send_invoices_to_clients(invoiceAccount.value)
            );
        });
        const canMarkUnpaidInvoice = computed(() => {
            return (
                !!state.current_invoice && state.current_invoice.id > 0 && canEditAccount.value
                && (state.current_invoice.status === "paid")
            );
        });

        const onClearErrorsClick = () => {
            clearProcessErrors(state.current_invoice.account_id, [state.current_invoice.id])
                .then(items => {
                    if (items.length > 0) {
                        state.current_invoice.process_status = items[0].process_status;
                        state.current_invoice._data = items[0].data;
                    }
                })
                .catch(ex => errorDialog.showError(ex));
        };

        // listen for export results
        const onMessage = (data) => {
            if (data.invoice_id === state.current_invoice.id) {
                loadInvoice(data.invoice_id)
            }
        }
        const exportWebSocket = new WS(onMessage, "/ws/export")
        const importWebSocket = new WS(onMessage, "/ws/import")
        exportWebSocket.connect()
        onUnmounted(() => {
            exportWebSocket.close()
            importWebSocket.close()
        })


        const onMarkPaidClick = () => {
            let selected_invoices_id_list = selected_invoices_list.value.map(x => x.id);

            markPaidInvoices(state.current_invoice.account_id, selected_invoices_id_list)
                .then((invoices) => {
                    const invoice = invoices.find(x => x.id === state.current_invoice.id);
                    if (invoice) {
                        state.current_invoice.status = invoice.status;
                        state.current_invoice.paid_at = invoice.paid_at
                    }
                })
                .catch(ex => errorDialog.showError(ex));
        }
        const onMarkUnpaidClick = () => {
            let selected_invoices_id_list = selected_invoices_list.value.map(x => x.id);

            markUnpaidInvoices(state.current_invoice.account_id, selected_invoices_id_list)
                .then((invoices) => {
                    const invoice = invoices.find(x => x.id === state.current_invoice.id);
                    if (invoice) {
                        state.current_invoice.status = invoice.status;
                    }
                })
                .catch(ex => errorDialog.showError(ex));
        }

        const onCreateInvoiceClick = () => {
            return save_invoice();
        };

        const onDownloadInvoiceClick = id => {
            if (parseInt(id)) window.open("/invoices/" + id + "/download");
        }

        const canConfirmInvoiceRows = computed(() => {
            const is_invoice_locked = ['created', 'sent', 'paid'].includes(state.current_invoice.status)
            return !!(is_invoice_locked && can_complete_invoice_rows(invoiceAccount.value))
        })

        const canEditInvoice = computed(() => {
            const can_edit_invoice = can_manage_client_invoices(invoiceAccount.value, state.current_invoice.client_id)
            if (myDebug.level()) console.warn('canEditInvoice', can_edit_invoice)
            return can_edit_invoice
        })

        let headers = computed(() => {
            let headers = [
                {
                    text: translations.value.description,
                    value: "description",
                    sortable: false,
                    class: "g5--text font-weight-medium",
                },
                {
                    text: translations.value.quantity,
                    value: "quantity",
                    sortable: false,
                    class: "g5--text font-weight-medium header-style",
                    align: "end",
                },
                {
                    text: translations.value.unit,
                    value: "unit",
                    sortable: false,
                    class: "g5--text font-weight-medium header-style",
                    align: "left",
                },
                {
                    text: translations.value.net_price,
                    value: "net_price",
                    sortable: false,
                    class: "g5--text font-weight-medium header-style",
                    align: "end",
                },
                {
                    text: translations.value.discount_rate,
                    value: "discount_rate",
                    sortable: false,
                    class: "g5--text font-weight-medium header-style",
                    align: "end",
                },
                {
                    text: translations.value.tax_name + ' %',
                    value: "tax_sum",
                    sortable: false,
                    class: "g5--text font-weight-medium header-style",
                    align: "end",
                },
                {
                    text: translations.value.net_sum,
                    value: "net_sum",
                    sortable: false,
                    class: "g5--text font-weight-medium header-style",
                    align: "end",
                }
            ]

            headers.push({
                text: '',
                value: '',
                sortable: false,
                align: "end",
                class: "g5--text font-weight-medium",
                width: "0"
            })

            return headers;
        });

        const customFilter = (item, queryText) => {
            const textOne = item.name.toLowerCase();
            const searchText = queryText.toLowerCase();
            return textOne.indexOf(searchText) > -1;
        };
        const addRow = () => {
            return state.current_invoice.rows.push({
                is_new_row: true,

                description: "",
                net_price: null,
                quantity: 1,
                discount_rate: 0,
                unit: "",
                net_sum: null,
                tax_sum: null,

                tax_id: null,
                article_id: null,
                resource_id: null,
                billing_project_id: null,
                cost_center_id: null,

                ext_variable: null,
                person_id: current_account.value.id,
            });
        };
        const onDeleteRow = index => {
            state.current_invoice.rows.splice(index, 1);
            if (state.current_invoice.rows.length === 0) {
                return addRow();
            }
        };

        const onCancelEditClick = () => {
            state.isEditing = !state.isEditing;
            if (state.current_invoice.id) {
                loadInvoice(state.current_invoice.id)
            } else {
                navigateToList();
            }
        }

        const onShowAllLinesClick = () => {
            const show_all_lines = 1
            loadInvoice(state.current_invoice.id, show_all_lines)
            state.isEditing = true
        };

        const onEditInvoiceClick = () => {
            if (!isFeatureEnabled(state.current_invoice.account_id, "billing")) return;
            const isEditing = !state.isEditing;
            if (isEditing) {
                // active seller
                const billing_settings = clientBillingSettings.value.billing_settings
                state.current_invoice.seller_name = billing_settings.seller_name
                state.current_invoice.seller_address = billing_settings.seller_address
                state.current_invoice.seller_company_number = billing_settings.seller_company_number
                state.current_invoice.seller_vat_number = billing_settings.seller_vat_number
            }
            state.isEditing = isEditing
        };
        const onSaveClick = () => {
            if (!isFeatureEnabled(state.current_invoice.account_id, "billing")) return;

            setConfirmOptions({
                title: $t("Please confirm the changes you have made to this invoice."),
                text: $t(
                    "NB! Note that changed invoice will not be automatically sent to customer or exported to external systems - you will have to do this manually."
                ),
                confirm_text: $t("Confirm"),
                cancel_text: $t("Cancel"),
                action: save_invoice,
                icon: "error_outline"
            });
            openConfirmDialog();
        };
        const getSaveInvoiceData = () => {
            return {
                account_id: state.current_invoice.account_id,
                company_id: state.current_invoice.account_id,
                client_id: state.current_invoice.client_id,
                type: state.current_invoice.type,
                currency: state.current_invoice.currency,
                comments: state.current_invoice.comments,
                id: state.current_invoice.id,
                invoice_date: state.current_invoice.invoice_date,
                due_date: state.current_invoice.due_date,
                invoice_number: state.current_invoice.invoice_number,
                reference_number: state.current_invoice.reference_number,
                rows: updatedInvoiceRowOrder.value.length ? updatedInvoiceRowOrder.value : state.current_invoice.rows,
                _data: state.current_invoice._data
            }
        }

        const showInvoiceNumberExistsConfirm = () => {
            setConfirmOptions({
                title: $t("Warning"),
                text: $t(
                    "Invoice number already exists! Please confirm to save anyway."
                ),
                confirm_text: $t("Confirm"),
                cancel_text: $t("Cancel"),
                action: () => save_invoice_confirmed(true),
                icon: "error_outline"
            });
            openConfirmDialog();
        }

        const save_invoice_confirmed = async (force = false) => {
            state.in_progress_confirmed = true;

            hide_validation_errors();

            let saveInvoiceData = getSaveInvoiceData()

            if (state.current_invoice.id) {
                saveSalesInvoice(saveInvoiceData, force)
                    .then(function (invoice) {
                        if (invoice.err_type === 'same_invoice_number_exists') {
                            showInvoiceNumberExistsConfirm()
                            return
                        }

                        state.current_invoice = formatInvoiceDates(invoice);
                        state.isEditing = false;
                    })
                    .catch(function (error) {
                        if (error && error.response && error.response.data && error.response.data.error_type === "form_validation") {
                            show_validation_errors(error.response.data.errors);
                            return;
                        }
                        errorDialog.showError(error);
                    });
            } else {
                ApiInvoice.createInvoiceFromClient(saveInvoiceData, force)
                    .then((data) => {
                        if (data.err_type === 'same_invoice_number_exists') {
                            showInvoiceNumberExistsConfirm()
                            return
                        }
                        navigateToList();
                    })
                    .catch((error) => {
                        if (error && error.response && error.response.data && error.response.data.error_type === "form_validation") {
                            show_validation_errors(error.response.data.errors);
                            return;
                        }
                        errorDialog.showError(error);
                    })
            }
        };
        const show_validation_errors = errors => {
            const global_err = [];

            for (let err of Array.from(errors)) {
                if (!err.field) {
                    global_err.push(err.error_message);
                    continue;
                }
                let {field} = err;

                if (["tax_id"].includes(err.field)) field = "tax_id";

                if (err.index !== undefined) {
                    Vue.set(state.current_invoice.rows[err.index], field + "_error", err.error_message);
                } else {
                    Vue.set(state.current_invoice, field + "_error", err.error_message);
                }
            }

            if (global_err.length > 0) {
                setErrorOptions({
                    title: $t("Error in data"),
                    text: global_err.join("<br>")
                });
                openError();
            }
        };
        const save_invoice = () => {
            if (state.current_invoice.status === "in_progress" && !state.in_progress_confirmed) {
                setTimeout(() => {
                    setConfirmOptions({
                        title: $t("Attention!"),
                        text: $t(
                            "The invoicing period is still ongoing. Any work done in this period after creation of the invoice will not be invoiced."
                        ),
                        confirm_text: $t("Confirm"),
                        cancel_text: $t("Cancel"),
                        action: save_invoice_confirmed,
                        icon: "error_outline"
                    });
                    openConfirmDialog();
                }, 200);
                return;
            }

            save_invoice_confirmed();
        };
        const formatInvoiceDates = invoice => {
            Vue.set(invoice, "invoice_date", invoice.invoice_date ? invoice.invoice_date.substring(0, 10) : null);
            Vue.set(invoice, "due_date", invoice.due_date ? invoice.due_date.substring(0, 10) : null);
            return invoice;
        };
        const hide_validation_errors = () => {
            state.current_invoice.invoice_number_error = null
            state.current_invoice.invoice_date_error = null
            state.current_invoice.client_id_error = null
            state.current_invoice.due_date_error = null

            return (() => {
                const result = [];
                for (let row of Array.from(state.current_invoice.rows)) {
                    row.description_error = null
                    row.quantity_error = null
                    row.net_price_error = null

                    if (row.tax_error) {
                        result.push((row.tax_error = ""));
                    } else {
                        result.push(undefined);
                    }
                }
                return result;
            })();
        };

        const {onSendInvoicesClick} = SendInvoicesDialog(0, selected_invoices_list);
        const {onExportInvoicesClick} = ExportInvoicesDialog(0, selected_invoices_list);
        const {onDeleteInvoicesClick} = DeleteInvoicesDialog(0, selected_invoices_list);

        const onVoidInvoiceClick = function () {
            setConfirmOptions({
                title: $t("Are you sure?"),
                confirm_text: $t("Yes"),
                cancel_text: $t("No"),
                action: function () {
                    cancelSalesInvoices(state.current_invoice.account_id, [state.current_invoice.id])
                        .then(response => {
                            loadInvoice(state.current_invoice.id)
                        })
                        .catch(ex => errorDialog.showError(ex));
                }
            });
            openConfirmDialog();
        };

        const onRestoreInvoiceClick = function () {
            setConfirmOptions({
                title: $t("Are you sure?"),
                confirm_text: $t("Yes"),
                cancel_text: $t("No"),
                action: function () {
                    restoreSalesInvoices(state.current_invoice.account_id, [state.current_invoice.id])
                        .then(response => {
                            loadInvoice(state.current_invoice.id)
                        })
                        .catch(ex => errorDialog.showError(ex));
                }
            });
            openConfirmDialog();
        };

        const onUpdateInvoiceStatusClick = async () => {
            try {
                await importWebSocket.connect()
                await ApiInvoice.updateInvoiceStatuses({
                    integration_vendor_name: props.vendorType,
                    account_id: state.current_invoice.account_id,
                    invoice_id_list: [invoiceId]
                })
                state.current_invoice.process_status = 'import_invoice'
            } catch (e) {
                errorDialog.showError(e)
            }
        }

        const fetchTaxes = async (company_id) => {
            state.taxes = await ApiTax.getTaxes(company_id)
        }

        const addInvoice = async () => {
            isLoaded.value = false
            try {
                const client_id = clientId.value

                // client
                invoiceClient.value = await fetchClient({client_id: client_id})
                setCurrentClient(invoiceClient.value)
                const company_id = invoiceClient.value.account_id
                // company
                invoiceAccount.value = available_accounts.value.find(x => x.id === company_id)

                await Promise.all([
                    fetchProducts(company_id),
                    fetchAccountProductFields(company_id),
                    fetchTaxes(company_id)
                ]);

                let invoice = {
                    rows: [],
                    id: null,
                    type: "sales",
                    account_id: company_id,
                    client_id: client_id,
                    client: invoiceClient.value,
                    currency: invoiceAccount.value.currency,
                    locale_code: invoiceClient.value && invoiceClient.value.locale_code ? invoiceClient.value.locale_code : invoiceAccount.value.locale_code,
                    contract_id: null,
                    created_at: null,
                    due_date: null,
                    exported_at: null,
                    invoice_date: formatSysDate(new Date()),
                    invoice_number: "",
                    comments: "",
                    net_sum: 0,
                    tax_sum: 0,
                    paid_sum: 0,
                    total_sum: 0,
                    paid_at: null,
                    payment_subscription_id: null,
                    period_label: "",
                    process_status: null,
                    product_fields: null,
                    reference_number: "",
                    sent_at: null,
                    sent_from: "Uku",
                    status: "pending",
                    updated_at: null,
                    _data: null,
                }

                const invoice_is_locked = ['created', 'sent', 'paid', 'annulled'].includes(invoice.status)
                const force_locale_code = invoice_is_locked ? invoice.locale_code : invoiceClient.value.locale_code

                clientBillingSettings.value = await fetchClientBillingSettings(client_id, force_locale_code)

                invoice.currency = clientBillingSettings.value.billing_settings.currency
                translations.value = clientBillingSettings.value.translations

                state.current_invoice = ref(formatInvoiceDates(invoice))
                setInvoiceSeller()

                addRow();
                state.isEditing = true;
                isLoaded.value = true
            } catch (e) {
                errorDialog.showError(e)
            }
        }

        const loadInvoice = async (invoice_id, show_all_lines) => {
            isLoaded.value = false
            try {
                // invoice
                show_all_lines = show_all_lines || new URLSearchParams(window.location.search).get('show_all_lines')
                const invoice = await fetchInvoice(invoice_id, show_all_lines)
                const client_id = invoice.client_id

                // client
                invoiceClient.value = await fetchClient({client_id: client_id})
                setCurrentClient(invoiceClient.value)
                const company_id = invoice.account_id
                // company
                invoiceAccount.value = available_accounts.value.find(x => x.id === company_id)

                await Promise.all([
                    fetchProducts(company_id),
                    fetchAccountProductFields(company_id),
                    fetchTaxes(company_id)
                ]);

                const invoice_is_locked = ['created', 'sent', 'paid', 'annulled'].includes(invoice.status)
                const force_locale_code = invoice_is_locked ? invoice.locale_code : invoiceClient.value.locale_code
                clientBillingSettings.value = await fetchClientBillingSettings(client_id, force_locale_code)
                translations.value = clientBillingSettings.value.translations

                state.current_invoice = formatInvoiceDates(invoice)
                setInvoiceSeller()

                const autoStartExport = () => {
                    const openAppRun = router.currentRoute.query.open_app_run
                    if (openAppRun === 'export') {
                        router.replace({query: {}})
                        onExportInvoicesClick()
                    }
                }
                autoStartExport()

                state.isEditing = false
                isLoaded.value = true
            } catch (e) {
                errorDialog.showError(e)
            }
        }

        const setInvoiceSeller = () => {
            const billing_settings = clientBillingSettings.value.billing_settings
            // seller
            state.current_invoice.seller_name = state.current_invoice.seller_name || billing_settings.seller_name
            state.current_invoice.seller_address = state.current_invoice.seller_address || billing_settings.seller_address
            state.current_invoice.seller_company_number = state.current_invoice.seller_company_number || billing_settings.seller_company_number
            state.current_invoice.seller_vat_number = state.current_invoice.seller_vat_number || billing_settings.seller_vat_number
            // client
            state.current_invoice.client_name = state.current_invoice.client_name || billing_settings.client_name
            state.current_invoice.client_address = state.current_invoice.client_address || billing_settings.client_address
            state.current_invoice.client_company_number = state.current_invoice.client_company_number || billing_settings.client_company_number
            state.current_invoice.client_vat_number = state.current_invoice.client_vat_number || billing_settings.client_vat_number
            // seller bank
            state.current_invoice.seller_bank_1 = billing_settings.seller_bank_1
            state.current_invoice.seller_iban_1 = billing_settings.seller_iban_1
            state.current_invoice.seller_swift_1 = billing_settings.seller_swift_1
            state.current_invoice.seller_bank_2 = billing_settings.seller_bank_2
            state.current_invoice.seller_iban_2 = billing_settings.seller_iban_2
            state.current_invoice.seller_swift_2 = billing_settings.seller_swift_2
            // logo
            state.current_invoice.seller_logo_url = billing_settings.seller_logo_url
            // new invoice
            state.current_invoice.due_date = state.current_invoice.due_date || formatSysDate(addDays(new Date(), billing_settings.invoice_due_date_period))
            state.current_invoice.currency = state.current_invoice.currency || billing_settings.currency
            state.current_invoice.comments = state.current_invoice.comments || billing_settings.description
        }

        onMounted(async () => {
            if (invoiceId) {
                await loadInvoice(invoiceId)
            } else {
                await addInvoice()
            }
            loadBreadcrumbs();
        });

        const loadBreadcrumbs = () => {
            setBreadcrumbs([
                {
                    to: {
                        name: 'billing',
                        query: {
                            account_id: state.current_invoice.account_id
                        }
                    },
                    name: $t("Billing")
                },
                {
                    to: {
                        name: 'client_profile_billing',
                        params: {
                            id: state.current_invoice.client_id,
                            accountId: state.current_invoice.account_id
                        }
                    },
                    name: state.current_invoice.client_name
                }
            ], ctx.root._route.name)
        }

        const productFieldResources = computed(() => {
            if (state.current_invoice.product_fields) {
                return state.current_invoice.product_fields.filter(x => x.type === PRODUCT_FIELD_TYPES.RESOURCE)
            }
            return []
        })

        const updatedInvoiceRowOrder = ref(0)

        function onUpdate(items) {
            updatedInvoiceRowOrder.value = items
        }

        const selectedVendorName = computed(() => {
            if (invoiceAccount.value && state.current_invoice && state.current_invoice.selected_vendor_type) {
                return getRegionAppTitle(state.current_invoice.selected_vendor_type, invoiceAccount.value.region_code)
            }

            return ''
        })

        return {
            ...toRefs(state),
            formatting,
            available_accounts,
            currency,
            is_processing,
            locale_code,
            account_editable,
            client_editable,
            has_errors,
            warning,
            total_net,

            isLoaded,
            translations,
            clientBillingSettings,

            taxesSummary,
            can_export_invoice,
            can_send_invoice,
            canMarkUnpaidInvoice,
            onMarkPaidClick,
            onMarkUnpaidClick,
            process_error_message,
            selected_account_obj,
            customFilter,
            addRow,
            onSendInvoicesClick,
            onCreateInvoiceClick,
            onDownloadInvoiceClick,
            onDeleteInvoicesClick,
            onExportInvoicesClick,
            onShowAllLinesClick,
            onDeleteRow,
            onEditInvoiceClick,
            onSaveClick,
            onClearErrorsClick,
            onVoidInvoiceClick,
            onRestoreInvoiceClick,
            getStatusColorMap,
            onCancelEditClick,
            onAddProductClick,
            canEditAccount,
            headers,
            clientId,
            accountId,
            invoiceClient,
            invoiceAccount,
            canConfirmInvoiceRows,
            productFieldResources,
            onUpdate,
            canEditInvoice,
            richTextEditor,
            can_update_invoice_status,
            onUpdateInvoiceStatusClick,
            showProductDialog,
            formatSysDate,
            formatMoney,
            is_open,
            getRegionAppTitle,
            selectedVendorName,
            can_send_invoices_to_clients
        };
    }
};
</script>

<style scoped lang="scss">
.no-break {
    white-space: nowrap;
}

.override {
    .tofrom {
        .comp-data {
            color: var(--v-g5-base);
            line-height: 20px;
        }

        .tofrom-caption {
            text-transform: uppercase;
            color: var(--v-main-base);
        }

        .tofrom-headline {
            font-size: 26px;
            letter-spacing: 0.5px;
        }
    }

    &.text-xs-right {
        textarea {
            text-align: right;
        }

        input {
            text-align: right;
        }
    }

    .v-input__slot {
        padding-left: 0 !important;
    }
}

.image-container {
    height: 176px;
    max-width: 176px;
    min-width: 176px;
    display: inline-block;

    ::v-deep img {
        width: 100%;
        height: 100%;
        object-fit: contain;
    }
}

::v-deep .quill-editor .ql-container.ql-disabled .ql-editor {
    border-color: transparent !important;
}

::v-deep .header-style {
    width: 120px !important;
    min-width: 0 !important;
    max-width: 120px !important;
}

</style>
